import { Box, styled, Typography } from "@mui/material";
import { MessageView } from "@my-helpers/common";
import ReactMarkdown from "react-markdown";
import { format } from 'date-fns';
import { ElementType } from "react";

export const MessageBubble = styled(Box)<{ role: string }>(({ theme, role }) => ({
	marginBottom: theme.spacing(0.5),
	padding: theme.spacing(1),
	margin: theme.spacing(1),
	borderRadius: theme.shape.borderRadius,
	maxWidth: '90%',
	alignSelf: role === 'user' ? 'flex-start' : 'flex-end',
	backgroundColor: theme.palette.grey[100],
	color: theme.palette.text.primary,
	marginLeft: role === 'user' ? 0 : 'auto',
	marginRight: role === 'user' ? 'auto' : 0,
}));

const MessageSender = styled(Typography)(({ theme }) => ({
	fontSize: '0.75rem',
	fontWeight: 600,
	marginBottom: theme.spacing(0.5),
	opacity: 0.8,
}));

const MessageTimestamp = styled(Typography)(({ theme }) => ({
	fontSize: '0.625rem',
	opacity: 0.6,
	marginTop: theme.spacing(0.5),
	textAlign: 'right',
}));

const MessageContent = styled(Box)(({ theme }) => ({
	fontSize: '0.75rem',
	padding: 0,
	'& p': {
		margin: `${theme.spacing(0.5)} 0`,
	},
	'& pre': {
		padding: theme.spacing(1),
		fontSize: 'inherit',
		fontFamily: 'monospace',
		whiteSpace: 'pre-wrap',
		wordWrap: 'break-word',
	},
	'& code': {
		padding: '2px 4px',
		borderRadius: '3px',
		fontSize: 'inherit',
		fontFamily: 'monospace',
		whiteSpace: 'pre-wrap',
		wordWrap: 'break-word',
	},
	'& h1, & h2, & h3, & h4, & h5, & h6': {
		margin: `${theme.spacing(1)} 0`,
	},
	'& ul, & ol': {
		paddingLeft: theme.spacing(2),
	},
}));

const MessageHeader = styled(Box)<{ expanded: string }>(
	({ theme, expanded }) => {
		return {
			position: 'relative',
			fontSize: theme.typography.caption.fontSize,
			fontWeight: theme.typography.fontWeightLight,
			border: `1px solid ${theme.palette.divider}`,
			borderRadius: theme.shape.borderRadius,
			backgroundColor: theme.palette.grey[50],
			padding: theme.spacing(0.5),
			'&:hover': {
				backgroundColor: theme.palette.grey[200],
			},
			'& > *': {
				margin: `${theme.spacing(0.50)} 0`,
			},
			...(expanded === 'true' && {
				height: 'auto',
			}),
			...(expanded === 'false' && {
				minWidth: 0,
				'& > *:first-of-type > *:first-child': {
					display: 'block',
					overflow: 'hidden',
					whiteSpace: 'nowrap !important',
					textOverflow: 'ellipsis',
					width: '100%'				
				},
				// Hide subsequent blocks within the markdown root
				'& *:not(:first-child)': {
					display: 'none !important',
				},
			}),
		}
	}
);

interface MessageProps {
	message: MessageView
	expandedThinkBlocks: Set<string>
	toggleThinkBlock: (id: string) => void
}

export const ThreadMessage: ElementType<MessageProps> = ({
	message, expandedThinkBlocks, toggleThinkBlock }) => {

	return (
		<MessageBubble
			key={message.id}
			role={message.sender.role}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					flexDirection: 'row',
					gap: 1
				}}
			>
				<MessageSender>
					{message.sender.name}
				</MessageSender>
				<MessageTimestamp>
					{format(new Date(message.created_at), 'PP pp')}
				</MessageTimestamp>
			</Box>
			{message.header && Object.keys(message.header).length > 0 && (
				<MessageHeader
					id={message.id}
					expanded={expandedThinkBlocks.has(message.id).toString()}
					onClick={() => toggleThinkBlock(message.id)}>
					<ReactMarkdown>
					{Object.entries(message.header)
						.map(([key, value]) => `*${key}:*\n\n${value}`)
						.join('\n\n---\n\n')}
					</ReactMarkdown>
				</MessageHeader>
			)}
			<MessageContent>
				<ReactMarkdown>
					{message.body}
				</ReactMarkdown>
			</MessageContent>
			{message.footer && Object.keys(message.footer).length > 0 && (
				<MessageHeader
					id={message.id}
					expanded={expandedThinkBlocks.has(message.id).toString()}
					onClick={() => toggleThinkBlock(message.id)}>
					<ReactMarkdown>
					{Object.entries(message.footer)
						.map(([key, value]) => `*${key}:*\n\n${value}`)
						.join('\n\n---\n\n')}
					</ReactMarkdown>
				</MessageHeader>
			)}
		</MessageBubble>
	)
} 